import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  DatasetCompleteResultSchema,
  DatasetCreateResultSchema,
  DatasetFetchResultSchema,
  DatasetQuerySchema,
  DatasetResumeResultSchema,
  DatasetUniqueResultSchema,
  DatasetUploadCompleteSchema,
  DatasetUploadResumeSchema,
  OrderBySchema,
  PriceFetchSchema,
  StartingMaterialDatasetSchema,
} from './starting-materials-schema';
import { PaginationSchema } from '../common';

const c = initContract();
const pathPrefix = '/starting-material';

export const startingMaterialContract = c.router(
  {
    fetchPredefinedDatasets: {
      method: 'GET',
      path: '/dataset/predefined',
      responses: {
        200: z.array(DatasetUniqueResultSchema),
      },
    },
    fetchPrivateDatasets: {
      method: 'GET',
      path: '/dataset/private',
      query: PaginationSchema.merge(
        z.object({
          where: DatasetQuerySchema.partial()
            .merge(z.object({ uploadedAt: z.object({ gt: z.coerce.date().optional() }) }))
            .optional(),
          orderBy: OrderBySchema.optional(),
        })
      ),
      responses: {
        200: DatasetFetchResultSchema,
      },
    },
    fetchUniqueDataset: {
      method: 'GET',
      path: '/dataset/:id',
      pathParams: z.object({
        id: StartingMaterialDatasetSchema.shape.id,
      }),
      responses: {
        200: DatasetUniqueResultSchema,
      },
    },
    createDatasetUpload: {
      method: 'POST',
      path: '/dataset',
      body: StartingMaterialDatasetSchema.omit({
        ownerId: true,
        createdAt: true,
        uploadedAt: true,
      }).partial({
        id: true,
        description: true,
      }),
      responses: {
        201: DatasetCreateResultSchema,
      },
    },
    resumeDatasetUpload: {
      method: 'PATCH',
      path: '/dataset/:id/resume',
      pathParams: z.object({
        id: StartingMaterialDatasetSchema.shape.id,
      }),
      body: DatasetUploadResumeSchema.omit({ ownerId: true }),
      responses: {
        200: DatasetResumeResultSchema,
      },
    },
    completeDatasetUpload: {
      method: 'PATCH',
      path: '/dataset/:id/complete',
      pathParams: z.object({
        id: StartingMaterialDatasetSchema.shape.id,
      }),
      body: DatasetUploadCompleteSchema.omit({ ownerId: true }),
      responses: {
        200: DatasetCompleteResultSchema,
      },
    },
    removeDatasets: {
      method: 'DELETE',
      path: '/dataset',
      body: z.array(StartingMaterialDatasetSchema.shape.id),
      responses: {
        200: z.array(StartingMaterialDatasetSchema.shape.id),
      },
    },
    fetchPricing: {
      method: 'GET',
      path: '/pricing',
      query: z.object({
        smilesList: z.array(z.string()),
        maxPrice: z.coerce.number().optional(),
        maxPricePerGram: z.coerce.number().optional(),
      }),
      responses: {
        200: PriceFetchSchema,
      },
    },
    fetchSuppliersLink: {
      method: 'POST',
      path: '/suppliers-link',
      headers: z.object({ authorization: z.string() }),
      body: z.object({ casNumber: z.string() }),
      responses: {
        200: z.object({ link: z.string() }),
      },
    },
  },
  {
    pathPrefix,
    validateResponseOnClient: true,
  }
);
